// Figma: "Loyalty Cloud" file, node 18428:54678 ("Edit resident details" dialog), opened
// from the "Edit" button in ./ResidentDetailDrawer.js. Resident records only expose a
// combined `name` (no first/last split) and there's no confirmed backend field mapping for
// this form yet (residentsSlice.js's updateResidentInfo mutation has no prior caller in this
// codebase), so Save is a stub for now — the dialog collects the edits but doesn't persist
// them until the real field contract is confirmed.
import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {Box, Button, Dialog, Stack, TextField, Typography} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

import {tokens} from '../../../themes/ThemeCustomization';
import calendarIcon from '../../../assets/residentDetail/calendar-icon.svg';
import buttonSpinnerIcon from '../../../assets/auth/button-spinner-icon.svg';
import SpinnerIcon from '../../authentication/components/SpinnerIcon';
import validateEmail from '../../../commons/utils/emailValidation';
import LeaseDateRangePicker from './LeaseDateRangePicker';

const splitName = (fullName) => {
    const parts = (fullName || '').trim().split(/\s+/);
    return {firstName: parts[0] || '', lastName: parts.slice(1).join(' ')};
};

// A phone number is valid once it has enough digits for a real number (10, or 11 with a
// leading country code digit) — formatting characters (spaces, dashes, parens, +) are ignored.
const validatePhone = (value) => {
    const digits = value.replace(/\D/g, '');
    return digits.length === 10 || digits.length === 11;
};

const FieldLabel = ({children}) => (
    <Typography variant="body2" sx={{color: tokens.color.neutral950, pb: 1}}>
        {children}
    </Typography>
);

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: tokens.radius.control,
        fontSize: '14px',
        '& fieldset': {borderColor: tokens.color.neutral200},
        '&:hover fieldset': {borderColor: tokens.color.neutral300},
        '&.Mui-focused fieldset': {borderColor: tokens.color.sky600, borderWidth: '1px'}
    }
};

const errorInputSx = {
    ...inputSx,
    '& .MuiOutlinedInput-root': {
        ...inputSx['& .MuiOutlinedInput-root'],
        '& fieldset': {borderColor: tokens.color.error500},
        '&:hover fieldset': {borderColor: tokens.color.error500},
        '&.Mui-focused fieldset': {borderColor: tokens.color.error500, borderWidth: '1px'}
    }
};

const EditResidentDialog = ({open, resident, onClose, onSave, saving}) => {
    const {firstName: initialFirstName, lastName: initialLastName} = splitName(resident?.name);

    const [firstName, setFirstName] = React.useState(initialFirstName);
    const [lastName, setLastName] = React.useState(initialLastName);
    const [email, setEmail] = React.useState(resident?.email || '');
    const [phone, setPhone] = React.useState(resident?.phone || '');
    const [unit, setUnit] = React.useState(resident?.unit || '');
    const [rent, setRent] = React.useState(resident?.rent ?? '');
    const [note, setNote] = React.useState(resident?.notes || '');
    const [leaseStart, setLeaseStart] = React.useState(resident?.leaseStart ? dayjs(resident.leaseStart) : null);
    const [leaseEnd, setLeaseEnd] = React.useState(resident?.leaseEnd ? dayjs(resident.leaseEnd) : null);
    const [dateAnchorEl, setDateAnchorEl] = React.useState(null);
    const [emailTouched, setEmailTouched] = React.useState(false);
    const [phoneTouched, setPhoneTouched] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        const {firstName: fn, lastName: ln} = splitName(resident?.name);
        setFirstName(fn);
        setLastName(ln);
        setEmail(resident?.email || '');
        setPhone(resident?.phone || '');
        setUnit(resident?.unit || '');
        setRent(resident?.rent ?? '');
        setNote(resident?.notes || '');
        setLeaseStart(resident?.leaseStart ? dayjs(resident.leaseStart) : null);
        setLeaseEnd(resident?.leaseEnd ? dayjs(resident.leaseEnd) : null);
        setEmailTouched(false);
        setPhoneTouched(false);
    }, [open, resident]);

    if (!resident) return null;

    const leaseDateRange =
        leaseStart && leaseEnd ? `${leaseStart.format('MMM D, YYYY')} - ${leaseEnd.format('MMM D, YYYY')}` : 'Select lease dates';

    const emailError = emailTouched && !validateEmail(email.trim());
    const phoneError = phoneTouched && !validatePhone(phone);
    const canSave = validateEmail(email.trim()) && validatePhone(phone);

    const handleSave = () => {
        setEmailTouched(true);
        setPhoneTouched(true);
        if (!canSave) return;
        onSave?.({firstName, lastName, email, phone, unit, rent, note, leaseStart, leaseEnd});
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '540px',
                    maxHeight: '720px',
                    borderRadius: '12px',
                    border: `1px solid ${tokens.color.neutral300}`
                }
            }}
        >
            <Box sx={{p: '20px', overflowY: 'auto'}}>
                <Typography sx={{fontSize: '20px', fontWeight: 600, lineHeight: '28px', color: tokens.color.neutral950, pb: '20px'}}>
                    Edit resident details
                </Typography>

                <Stack spacing={1.5} sx={{opacity: saving ? 0.6 : 1, pointerEvents: saving ? 'none' : 'auto'}}>
                    <Stack direction="row" spacing={1.5}>
                        <Box sx={{flex: 1}}>
                            <FieldLabel>First name</FieldLabel>
                            <TextField fullWidth size="small" value={firstName} onChange={(e) => setFirstName(e.target.value)} sx={inputSx} />
                        </Box>
                        <Box sx={{flex: 1}}>
                            <FieldLabel>Last name</FieldLabel>
                            <TextField fullWidth size="small" value={lastName} onChange={(e) => setLastName(e.target.value)} sx={inputSx} />
                        </Box>
                    </Stack>

                    <Box>
                        <FieldLabel>Email</FieldLabel>
                        <TextField
                            fullWidth
                            size="small"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onBlur={() => setEmailTouched(true)}
                            sx={emailError ? errorInputSx : inputSx}
                        />
                        {emailError && (
                            <Typography variant="caption" sx={{color: tokens.color.error500, display: 'block', pt: 0.5}}>
                                Please enter a valid email address
                            </Typography>
                        )}
                    </Box>

                    <Box>
                        <FieldLabel>Phone number</FieldLabel>
                        <TextField
                            fullWidth
                            size="small"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            onBlur={() => setPhoneTouched(true)}
                            sx={phoneError ? errorInputSx : inputSx}
                        />
                        {phoneError && (
                            <Typography variant="caption" sx={{color: tokens.color.error500, display: 'block', pt: 0.5}}>
                                Please enter a valid phone number
                            </Typography>
                        )}
                    </Box>

                    <Box>
                        <FieldLabel>Apartment, Unit, etc</FieldLabel>
                        <TextField fullWidth size="small" value={unit} onChange={(e) => setUnit(e.target.value)} sx={inputSx} />
                    </Box>

                    <Stack direction="row" spacing={1.5}>
                        <Box sx={{flex: 1}}>
                            <FieldLabel>Rent amount</FieldLabel>
                            <TextField
                                fullWidth
                                size="small"
                                value={rent}
                                onChange={(e) => setRent(e.target.value)}
                                InputProps={{startAdornment: <Typography sx={{color: tokens.color.neutral950, pr: 0.5}}>$</Typography>}}
                                sx={inputSx}
                            />
                        </Box>
                        <Box sx={{flex: 1}}>
                            <FieldLabel>Lease date</FieldLabel>
                            <TextField
                                fullWidth
                                size="small"
                                value={leaseDateRange}
                                onClick={(e) => setDateAnchorEl(e.currentTarget)}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: <Box component="img" src={calendarIcon} alt="" sx={{width: 16, height: 16}} />
                                }}
                                sx={{...inputSx, cursor: 'pointer', '& .MuiInputBase-input': {cursor: 'pointer'}}}
                            />
                            <LeaseDateRangePicker
                                open={Boolean(dateAnchorEl)}
                                anchorEl={dateAnchorEl}
                                onClose={() => setDateAnchorEl(null)}
                                startDate={leaseStart}
                                endDate={leaseEnd}
                                onChange={({start, end}) => {
                                    setLeaseStart(start);
                                    setLeaseEnd(end);
                                    if (start && end) setDateAnchorEl(null);
                                }}
                            />
                        </Box>
                    </Stack>

                    <Box>
                        <FieldLabel>Note</FieldLabel>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            sx={{...inputSx, '& .MuiOutlinedInput-root': {...inputSx['& .MuiOutlinedInput-root'], minHeight: '80px'}}}
                        />
                    </Box>
                </Stack>
            </Box>

            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    p: 1.5,
                    backgroundColor: tokens.color.neutral50,
                    borderTop: `1px solid ${tokens.color.neutral200}`
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={saving}
                    sx={{
                        height: 32,
                        px: 1.5,
                        color: tokens.color.neutral950,
                        borderColor: tokens.color.neutral200,
                        '&.Mui-disabled': {opacity: 0.6, color: tokens.color.neutral950, borderColor: tokens.color.neutral200}
                    }}
                >
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    loading={saving}
                    loadingPosition="start"
                    startIcon={<Box sx={{width: 0}} />}
                    loadingIndicator={
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                            <SpinnerIcon src={buttonSpinnerIcon} size={12} />
                            <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.white}}>
                                Save
                            </Typography>
                        </Stack>
                    }
                    sx={{
                        backgroundColor: tokens.color.sky600,
                        height: 32,
                        px: 1.5,
                        '&.Mui-disabled': {backgroundColor: tokens.color.sky600, opacity: 0.6}
                    }}
                >
                    Save
                </LoadingButton>
            </Stack>
        </Dialog>
    );
};

EditResidentDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    resident: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    saving: PropTypes.bool
};

export default EditResidentDialog;
